import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Create the context
const FilterContext = createContext();

// Helper function to parse URL parameters
const getFilterFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    return {
        price: [
            Number(params.get('minPrice')) || 0,
            Number(params.get('maxPrice')) || 1000000
        ],
        location: params.get('location') || '',
        locationRadius: Number(params.get('locationRadius')) || 0,
        bedrooms: [
            Number(params.get('minBedrooms')) || 0,
            Number(params.get('maxBedrooms')) || 10
        ],
        bathrooms: [
            Number(params.get('minBathrooms')) || 0,
            Number(params.get('maxBathrooms')) || 10
        ],
        propertyType: params.get('propertyType') || '',
        propertySubtype: params.get('propertySubtype') || '',
        lotSize: [
            Number(params.get('minLotSize')) || 0,
            Number(params.get('maxLotSize')) || 10000
        ],
        livingSpace: [
            Number(params.get('minLivingSpace')) || 0,
            Number(params.get('maxLivingSpace')) || 500
        ],
        buildYear: [
            Number(params.get('minBuildYear')) || 1800,
            Number(params.get('maxBuildYear')) || new Date().getFullYear()
        ],
        offer_type: params.get('offer_type') || 'BOTH'
    };
};

// Create the provider component
export function FilterProvider({ children }) {
    const initialFilters = getFilterFromURL();
    const [filters, setFilters] = useState(initialFilters);

    const updateFilter = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const location = useLocation();

    useEffect(() => {
      // re-check query params on every route change
      setFilters(getFilterFromURL());
    }, [location]);
    
    useEffect(() => {
        // Check if the current page is the Angebots page
        if (window.location.pathname.includes('/immobilien')) {
            // Sync filter changes with the URL
            const params = new URLSearchParams();
            params.set('minPrice', filters.price[0]);
            params.set('maxPrice', filters.price[1]);
            if (filters.location) params.set('location', filters.location);
            if (filters.locationRadius) params.set('locationRadius', filters.locationRadius);
            if (filters.bedrooms) {
                params.set('minBedrooms', filters.bedrooms[0]);
                params.set('maxBedrooms', filters.bedrooms[1]);
            }
            if (filters.bathrooms) {
                params.set('minBathrooms', filters.bathrooms[0]);
                params.set('maxBathrooms', filters.bathrooms[1]);
            }
            if (filters.propertyType) params.set('propertyType', filters.propertyType);
            if (filters.propertySubtype) params.set('propertySubtype', filters.propertySubtype);
            if (filters.lotSize) {
                params.set('minLotSize', filters.lotSize[0]);
                params.set('maxLotSize', filters.lotSize[1]);
            }
            if (filters.livingSpace) {
                params.set('minLivingSpace', filters.livingSpace[0]);
                params.set('maxLivingSpace', filters.livingSpace[1]);
            }
            if (filters.buildYear) {
                params.set('minBuildYear', filters.buildYear[0]);
                params.set('maxBuildYear', filters.buildYear[1]);
            }
            if (filters.offer_type) params.set('offer_type', filters.offer_type);
    
            window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
        } else {
            // Remove query parameters when not on the Angebots page
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [
        filters.price, filters.location, filters.locationRadius, filters.bedrooms,
        filters.bathrooms, filters.propertyType, filters.propertySubtype,
        filters.lotSize, filters.livingSpace, filters.buildYear, filters.offer_type
    ]);
    
    return (
        <FilterContext.Provider value={{ filters, updateFilter }}>
            {children}
        </FilterContext.Provider>
    );
}

// Custom hook to access the context
export function useFilters() {
    return useContext(FilterContext);
}
